"use client";
/**
 * Everything the search row could not carry.
 *
 * The pane opens with the picture where there is one, capped at the measured
 * 300-pixel long edge every captured asset shares — a hero running the full
 * column would be a large frame holding a small photograph.
 *
 * Below it the pane splits into what each endpoint alone can answer. A recipe
 * spends the pane on its ingredients and its directions, because those are the
 * fields the billed detail call bought and the search row published as `null`.
 * A food spends it on its SERVINGS: the captured record carries ten of them —
 * `100 g`, `1 cup cooked, diced`, `1 oz, with bone cooked (yield after bone
 * removed)` — each with its own full panel, and the row above could show
 * exactly one.
 *
 * The micronutrient basis is stated inside every panel rather than once at the
 * top, because a reader who scrolls to the fourth serving has left the sentence
 * behind, and `28` means micrograms here and per cent one card over.
 */
import type { CSSProperties, ReactElement } from "react";
import { IconSalad } from "@tabler/icons-react";
import {
  CresultcardDisclosure,
  CresultcardDetail,
  CresultcardIdentity,
  CresultcardListRow,
  CresultcardProse,
  CresultcardProvenance,
  CresultcardSectionHeader,
  CresultcardStack,
  CRC_TEXT_FAINT,
  CRC_TYPE_STYLE,
  CresultcardActionTone,
  CresultcardAlign,
  CresultcardFigureScale,
  CresultcardIdentitySlot,
  CresultcardMediaScale,
  CresultcardProseMode,
  CresultcardStackDensity,
  CresultcardTextStep,
  CresultcardTitleLines,
  useCresultcardLightbox,
  useCresultcardVariant,
  type CresultcardActionSpec,
} from "@/components/ui/Cresultcard";
import {
  CNUTRITIONCARD_DIRECTIONS_TITLE,
  CNUTRITIONCARD_INGREDIENTS_TITLE,
  CNUTRITIONCARD_OPEN_LABEL,
  CNUTRITIONCARD_SERVINGS_TITLE,
  C_NUTRITION_DETAIL_MEDIA_WIDTH,
  C_NUTRITION_KIND_LABEL,
  C_NUTRITION_SERVING_SCOPES,
  C_NUTRITION_SERVING_SCOPE_GROUP,
} from "./cnutritioncard.constants";
import { cnutritioncardLeadServing } from "./cnutritioncard.model";
import {
  CnutritioncardCategoryChips,
  CnutritioncardEnergy,
  CnutritioncardMetaLine,
  CnutritioncardShot,
} from "./CnutritioncardMeta";
import { CnutritioncardNutrientPanel } from "./CnutritioncardNutrientPanel";
import type { CnutritioncardDetailProps } from "./cnutritioncard.types";

/** Nothing yields here: the title is unclamped and the fact line measures itself. */
const DETAIL_TRUNCATION: readonly CresultcardIdentitySlot[] = [];
const SUMMARY_STYLE: CSSProperties = {
  ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
  flex: "0 0 auto",
  color: CRC_TEXT_FAINT,
};
/** The one serving already on the pane's own face. */
const LEAD_SERVING_INDEX = 0;

export function CnutritioncardDetail({
  data,
  context,
  bounds,
  provenance,
  servingScope,
  frame,
}: CnutritioncardDetailProps): ReactElement {
  const { value: registryScope } = useCresultcardVariant({
    group: C_NUTRITION_SERVING_SCOPE_GROUP,
    options: C_NUTRITION_SERVING_SCOPES,
  });
  const { open, viewer } = useCresultcardLightbox({ assets: data.images });
  const leadServing = cnutritioncardLeadServing(data);
  const alternateServings = data.servings.slice(LEAD_SERVING_INDEX + 1);
  const primaryAction: CresultcardActionSpec | undefined =
    data.href == null
      ? undefined
      : {
          href: data.href,
          label: CNUTRITIONCARD_OPEN_LABEL,
          tone: CresultcardActionTone.Primary,
        };

  return (
    <>
      <CresultcardDetail
        bounds={bounds}
        context={context}
        frame={frame}
        icon={IconSalad}
        label={C_NUTRITION_KIND_LABEL[data.kind]}
        primaryAction={primaryAction}
        provenance={
          <CresultcardProvenance
            primary={provenance}
          />
        }
      >
        <CresultcardStack
          density={CresultcardStackDensity.Regular}
        >
          {data.images.length > 0 && (
            <span
              // The cap yields to the pane: this detail is also a set's drill,
              // and in the Cards layout that pane is the card's own cell.
              style={{
                display: "block",
                width: C_NUTRITION_DETAIL_MEDIA_WIDTH,
              }}
            >
              <CnutritioncardShot
                data={data}
                onOpen={() => open(LEAD_SERVING_INDEX)}
                scale={CresultcardMediaScale.Hero}
              />
            </span>
          )}

          <CresultcardIdentity
            secondary={
              <CnutritioncardMetaLine
                data={data}
              />
            }
            title={data.name}
            titleLines={CresultcardTitleLines.Full}
            truncationOrder={DETAIL_TRUNCATION}
          />

          {/* The energy figure sits in the BODY, not in the identity's
              trailing slot, and the reason is the scope line under it.
              The basis can be the vendor's own
              `1 oz, with bone cooked (yield after bone removed)` — 53
              characters with its prefix — and a trailing slot beside a
              title is the one place on a card that cannot absorb that
              without taking the width from the name. In the body it gets
              the card's whole inner measure. `Cshoppingcard` puts its
              scope-bearing price in exactly the same region. */}
          <CnutritioncardEnergy
            align={CresultcardAlign.Start}
            data={data}
            scale={CresultcardFigureScale.Detail}
            servingScope={servingScope ?? registryScope}
          />

          {data.description != null && (
            <CresultcardProse
              displayStep={CresultcardTextStep.Body}
              mode={CresultcardProseMode.Display}
              text={data.description}
            />
          )}

          {data.summary != null && (
            <CresultcardProse
              displayStep={CresultcardTextStep.Meta}
              mode={CresultcardProseMode.Display}
              text={data.summary}
            />
          )}

          <CnutritioncardCategoryChips
            data={data}
          />

          {leadServing != null && (
            <CnutritioncardNutrientPanel
              serving={leadServing}
            />
          )}

          {data.ingredients.length > 0 && (
            <CresultcardStack
              density={CresultcardStackDensity.Snug}
            >
              <CresultcardSectionHeader
                title={CNUTRITIONCARD_INGREDIENTS_TITLE}
                trailing={
                  <span
                    style={SUMMARY_STYLE}
                  >
                    {String(data.ingredients.length)}
                  </span>
                }
              />
              {data.ingredients.map((ingredient, index) => (
                <CresultcardListRow
                  key={ingredient.foodId ?? `${String(index)}:${ingredient.name}`}
                  onLast={index === data.ingredients.length - 1}
                  subtitle={ingredient.description}
                  title={ingredient.name}
                  titleLines={CresultcardTitleLines.Two}
                />
              ))}
            </CresultcardStack>
          )}

          {data.directions.length > 0 && (
            <CresultcardStack
              density={CresultcardStackDensity.Snug}
            >
              <CresultcardSectionHeader
                title={CNUTRITIONCARD_DIRECTIONS_TITLE}
              />
              {data.directions.map((direction, index) => (
                <CresultcardListRow
                  key={direction}
                  onLast={index === data.directions.length - 1}
                  ordinal={index + 1}
                  title={direction}
                  titleLines={CresultcardTitleLines.Three}
                />
              ))}
            </CresultcardStack>
          )}

          {alternateServings.length > 0 && (
            <CresultcardStack
              density={CresultcardStackDensity.Snug}
            >
              <CresultcardSectionHeader
                title={CNUTRITIONCARD_SERVINGS_TITLE}
                trailing={
                  <span
                    style={SUMMARY_STYLE}
                  >
                    {String(data.servings.length)}
                  </span>
                }
              />
              {alternateServings.map((serving) => (
                <CresultcardDisclosure
                  key={serving.key}
                  label={serving.description ?? serving.key}
                  summary={
                    <CnutritioncardEnergy
                      align={CresultcardAlign.Start}
                      data={{ ...data, servings: [serving] }}
                      scale={CresultcardFigureScale.Row}
                      servingScope={servingScope ?? registryScope}
                    />
                  }
                >
                  <CnutritioncardNutrientPanel
                    serving={serving}
                  />
                </CresultcardDisclosure>
              ))}
            </CresultcardStack>
          )}
        </CresultcardStack>
      </CresultcardDetail>
      {viewer}
    </>
  );
}
